import * as React from 'react';
import { CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { useCampBulkWizardStepTour } from './useCampBulkWizardStepTour';

interface CampBulkWizardStepTourHelpProps {
  wizardStep: number;
  buttonLabel?: string;
}

/**
 * Help control for the bulk camp wizard — replays the guided tour for the current wizard step.
 */
export function CampBulkWizardStepTourHelp({
  wizardStep,
  buttonLabel = 'Tour this step',
}: CampBulkWizardStepTourHelpProps) {
  const startStepTour = useCampBulkWizardStepTour(wizardStep);
  const [open, setOpen] = React.useState(false);

  const handleStartTour = () => {
    startStepTour();
    window.requestAnimationFrame(() => setOpen(false));
  };

  const suggestion =
    wizardStep === 1
      ? 'New to the bulk camp wizard? Tour this step for naming tips before you continue.'
      : 'Need a refresher on this step? Replay the guided tour any time.';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0 text-muted-foreground"
          aria-label="Wizard step help and tour"
        >
          <CircleHelp className="h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3 p-3 text-sm">
        <p className="text-muted-foreground">{suggestion}</p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="w-full"
          onPointerDown={(event) => {
            event.preventDefault();
          }}
          onClick={handleStartTour}
        >
          {buttonLabel}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
